import { useProjectStore } from "@/stores/project-store";
import { captureDesktopChatAuthScopeGuard } from "./desktop-chat-auth-scope";
import {
  prepareScreenRecordings,
  type PreparedScreenRecordings,
} from "./screen-recording-attachments";
import {
  adoptScreenRecordingSessionIntoText,
  clearPreparedScreenRecordings,
  useScreenRecordingStore,
} from "./screen-recording-store";
import {
  clearMergedVoiceAttention,
  voiceAttentionRecordingIds,
} from "./voice-attention-merge";
import {
  type VoiceAttentionState,
  useVoiceAttentionStore,
} from "./voice-attention-store";
import type { DispatchVoicePromptArgs } from "./voice-dispatch";

function hasRecordingClips(recordingId: string): boolean {
  return Object.values(useScreenRecordingStore.getState().clips).some(
    (clip) => clip.attentionSessionId === recordingId,
  );
}

/** Settles an accepted prompt's recordings: the clips it carried are done,
 * any others return to the text draft, and its attention sessions end. */
export function acceptVoiceRecordings(
  attention: VoiceAttentionState,
  clipIds: string[],
): void {
  clearPreparedScreenRecordings(clipIds);
  for (const recordingId of voiceAttentionRecordingIds(attention)) {
    if (hasRecordingClips(recordingId))
      adoptScreenRecordingSessionIntoText(recordingId);
  }
  clearMergedVoiceAttention(attention);
}

/** Finishes a voice prompt's held recordings. A failed or abandoned
 * preparation calls `retract` once; an abandoned one resolves to null. */
export async function prepareVoiceRecordings(
  recordingIds: string[],
  attachmentCount: number,
  supportsImages: boolean,
  abandoned: () => boolean,
  retract: () => void,
): Promise<PreparedScreenRecordings | null> {
  let recordings: PreparedScreenRecordings;
  try {
    recordings = await prepareScreenRecordings(
      recordingIds,
      attachmentCount,
      abandoned,
      supportsImages,
    );
  } catch (error) {
    retract();
    throw error;
  }
  if (!abandoned()) return recordings;
  retract();
  return null;
}

/** Failed speech submissions retain their originals and cached sample IDs for
 * retry, while cancellation or a changed account/project discards the draft. */
export async function withVoiceRecordingDraft(
  args: DispatchVoicePromptArgs,
  dispatch: (args: DispatchVoicePromptArgs) => Promise<boolean>,
): Promise<boolean> {
  const recordingIds = voiceAttentionRecordingIds(
    args.attentionState ?? useVoiceAttentionStore.getState(),
  );
  const spaceId = useProjectStore.getState().currentSpace?.id;
  const currentAuth = captureDesktopChatAuthScopeGuard();
  let sent = false;
  try {
    sent = await dispatch(args);
    return sent;
  } finally {
    const keepForRetry =
      !sent &&
      currentAuth() &&
      !args.isCancelled?.() &&
      spaceId === useProjectStore.getState().currentSpace?.id;
    for (const recordingId of keepForRetry ? recordingIds : []) {
      if (hasRecordingClips(recordingId))
        adoptScreenRecordingSessionIntoText(recordingId);
    }
  }
}
